import { anyErrorFrameMatches } from './utils';

const valueIncludes = (error, str) => error.value?.indexOf(str) > -1;

const standardHttpErrors = [
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'conflict',
  'unprocessable entity',
  'proxy authentication required',
  'gateway timeout',
  'gateway time-out',
  'badgateway',
  'bad gateway',
  'internal server error',
  'server error',
  'service unavailable',
];

const errorHasNoInformation = error =>
  // If the entire value is a status code there's typically little we can ascertain from a new error.
  // In nearly all cases it's simply rethrown errors after we show a toast.
  standardHttpErrors.includes(error.value?.toLowerCase()) ||
  // AbortError appears on Firefox, we don't use AbortController anywhere yet so likely not us;
  // since we're also checking that there is no stacktrace this entry wouldn't be useful anyway
  error.type === 'AbortError' ||
  (error.value === 'No error message' && error.stacktrace?.frames?.length <= 2);

// Likely a SafeLink (email) crawler, should be safe to ignore:
const isNonErrorPromiseRejection = error =>
  error.value?.startsWith('Non-Error promise rejection captured with value:');

// Chunk loads on old bundles between releases has been tested, these are very likely network errors on the user's side:
const isChunkLoadError = error =>
  error.type === 'ChunkLoadError' ||
  /^Loading chunk \w+ failed/.test(error.value);

// Issue with browser extensions, especially browser blocking an extension's auto-fill:
const isExtensionOrIframeError = error =>
  valueIncludes(error, 'Blocked a frame with origin');

// Session timeouts are already handled in-app, was filtered in Sentry in the past still seems to catch things:
const isSessionTimeout = error => error.value === 'session timeout';

/**
 * Currently (maybe temporary) ignoring "Failed to fetch" errors as they produce a ton of noise.
 * In general we catch and handle API responses already, just we also rethrow in many places by
 * default to simplify adding further error handlers where appropriate.
 */
const isFailedToFetch = error =>
  (error.type === 'TypeError' && error.value === 'Failed to fetch') ||
  error.value?.startsWith('Non-Error promise rejection captured with value: ');

/**
 * PMs already know (some/most) browsers won't play sounds until user interacts with the page.
 * However, Dashboard is still to try to play sounds when events come in.
 *
 * Wrapping in a (working) try/catch isn't as easy in GWT as expected, so just going to ignore
 * the NotAllowedError until we migrate Dashboard to React... likely even beyond that.
 */
const isIgnorableMediaPlayerError = error =>
  valueIncludes(
    error,
    "play() failed because the user didn't interact with the document first",
  ) ||
  valueIncludes(
    error,
    'The play() request was interrupted by a call to pause()',
  ) ||
  valueIncludes(
    error,
    'The play method is not allowed by the user agent or the platform',
  );

// Cocoon errors that we're unlikely to fix:
const isIgnorableCocoonError = error =>
  anyErrorFrameMatches(
    error,
    frame => frame.function === 'DN_dynamicTeamDetailsForm',
  );

// Our code never gets the filename sanitized, check for that to see if
// it's a user's script (e.g. from browser extension or similar):
const errorIsFromBrowserExtensionOrUsersScript = error =>
  error.stacktrace?.frames?.every(
    frame => !frame.filename || frame.filename === '<anonymous>',
  );

// Is caused by an unsupported or legacy browser/device
const isLegacyUnsupportedBrowser = error =>
  // All modern / supported browsers support Intl.Locale:
  error.value === 'Intl.Locale is not a constructor' ||
  error.value?.startsWith(
    "undefined is not a constructor (evaluating 'new Intl.Locale(",
  );

export default function shouldFilterError(error) {
  return (
    errorHasNoInformation(error) ||
    errorIsFromBrowserExtensionOrUsersScript(error) ||
    isFailedToFetch(error) ||
    isChunkLoadError(error) ||
    isSessionTimeout(error) ||
    isIgnorableCocoonError(error) ||
    isExtensionOrIframeError(error) ||
    isNonErrorPromiseRejection(error) ||
    isIgnorableMediaPlayerError(error) ||
    isLegacyUnsupportedBrowser(error)
  );
}
